//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  Shuffle,
  Checkmark,
  Home as CarbonHome,
} from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { cn } from '../../components/ui/utils';
import type { Loop } from './SidebarTypes';

interface LoopSwitcherProps {
  expanded: boolean;
  activeLoopId: string | null;
  activeLoopName: string | null;
  completedLoops: Loop[];
  onLoopSelect: (loopId: string, loopName: string) => void;
  onAllLoopsClick: () => void;
  teamspaceName: string | undefined;
}

const getInitials = (name: string): string => {
  const words = name.split(/[\s-]+/);
  if (words.length >= 2 && words[0] && words[1]) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const LOOP_COLORS = [
  '#9C27B0',
  '#2196F3',
  '#F44336',
  '#4CAF50',
  '#FF9800',
  '#00BCD4',
  '#E91E63',
  '#3F51B5',
  '#009688',
  '#FFC107',
];

const getLoopColor = (index: number): string =>
  LOOP_COLORS[index % LOOP_COLORS.length] ?? '#9C27B0';

const LoopSwitcher: React.FC<LoopSwitcherProps> = ({
  expanded,
  activeLoopId,
  activeLoopName,
  completedLoops,
  onLoopSelect,
  onAllLoopsClick,
  teamspaceName,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // Don't close if clicking inside the switcher or the fixed flyout
      if (switcherRef.current?.contains(target)) return;
      if (flyoutRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = activeLoopName || t('select-loop');

  // Track the trigger button position so the fixed flyout can align to it
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const openCollapsedFlyout = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setFlyoutPos({ top: rect.top, left: rect.right + 8 });
    }
    setIsOpen((prev) => !prev);
  };

  if (!expanded) {
    return (
      <div
        className="flex flex-col items-center border-b border-gray-700 px-2 py-3"
        ref={switcherRef}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              ref={triggerRef}
              className="flex size-8 cursor-pointer items-center justify-center rounded-md text-white transition-colors hover:bg-gray-700"
              title={displayName}
              role="button"
              aria-label={`${t('loop-switcher')}: ${displayName}`}
              tabIndex={0}
              data-testid="loop-switcher-collapsed"
              onClick={openCollapsedFlyout}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCollapsedFlyout();
                }
              }}
            >
              <Shuffle size={20} />
            </div>
          </TooltipTrigger>
          {!isOpen && (
            <TooltipContent side="right">{displayName}</TooltipContent>
          )}
        </Tooltip>

        {isOpen && (
          <div
            ref={flyoutRef}
            className="fixed z-[1300] w-64 rounded-lg bg-[#262626] py-3 shadow-lg"
            style={{ top: flyoutPos.top, left: flyoutPos.left }}
            data-testid="loop-switcher-flyout"
          >
            <div className="px-4 pb-2 text-sm text-gray-400">
              {t('switch-loops')}
            </div>
            <div
              className="max-h-[300px] overflow-y-auto"
              style={{ scrollbarWidth: 'thin' }}
            >
              {completedLoops.map((loop, index) => (
                <button
                  key={loop.id}
                  onClick={() => {
                    onLoopSelect(
                      loop.id,
                      loop.displayName || loop.name || loop.id,
                    );
                    setIsOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 transition-colors hover:bg-gray-700"
                  data-testid={`loop-switcher-option-${loop.id}`}
                >
                  {activeLoopId === loop.id ? (
                    <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-green-600">
                      <Checkmark size={12} className="text-white" />
                    </div>
                  ) : (
                    <div className="size-4 shrink-0" />
                  )}
                  <div
                    className="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: getLoopColor(index) }}
                  >
                    {getInitials(loop.displayName || loop.name || loop.id)}
                  </div>
                  <span className="truncate text-sm text-white">
                    {loop.displayName || loop.name || loop.id}
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-2 border-t border-gray-700 pt-2">
              <button
                onClick={() => {
                  onAllLoopsClick();
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-white transition-colors hover:bg-gray-700"
                data-testid="loop-switcher-all-loops"
              >
                <CarbonHome size={16} className="text-white" />
                {t('all-loops-in')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <div className="border-b border-gray-700 px-3 py-3" ref={switcherRef}>
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between rounded-lg bg-gray-800 px-3 py-2 text-left transition-colors hover:bg-gray-700"
          aria-label={t('toggle-loop-switcher')}
          data-testid="loop-switcher-toggle"
        >
          <span
            className="flex min-w-0 flex-1 items-center gap-2"
            data-testid="loop-switcher-name"
          >
            <span className="shrink-0 text-gray-300">
              <Shuffle size={16} />
            </span>
            <span className="truncate text-sm font-medium text-gray-100">
              {displayName}
            </span>
          </span>
          <ChevronDown
            size={16}
            className={cn(
              'shrink-0 text-gray-400 transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </button>

        {isOpen && (
          <div
            className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg bg-[#262626] py-3 shadow-lg"
            data-testid="loop-switcher-dropdown"
          >
            <div className="px-4 pb-2 text-sm text-gray-400">
              {t('switch-loops')}
            </div>
            <div
              className="max-h-[300px] overflow-y-auto"
              style={{ scrollbarWidth: 'thin' }}
            >
              {completedLoops.map((loop, index) => (
                <button
                  key={loop.id}
                  onClick={() => {
                    onLoopSelect(
                      loop.id,
                      loop.displayName || loop.name || loop.id,
                    );
                    setIsOpen(false);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 transition-colors hover:bg-gray-700"
                  data-testid={`loop-switcher-option-${loop.id}`}
                >
                  {activeLoopId === loop.id ? (
                    <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-green-600">
                      <Checkmark size={12} className="text-white" />
                    </div>
                  ) : (
                    <div className="size-4 shrink-0" />
                  )}
                  <div
                    className="flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ backgroundColor: getLoopColor(index) }}
                  >
                    {getInitials(loop.displayName || loop.name || loop.id)}
                  </div>
                  <span className="truncate text-sm text-white">
                    {loop.displayName || loop.name || loop.id}
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-2 border-t border-gray-700 pt-2">
              <button
                onClick={() => {
                  onAllLoopsClick();
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-white transition-colors hover:bg-gray-700"
                data-testid="loop-switcher-all-loops"
              >
                <CarbonHome size={16} className="text-white" />
                {t('all-loops-in')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoopSwitcher;
